/**
 * INT-1522 (Fase 3/3 de INT-715): the per-workspace notice that a Meta
 * token went invalid, as it arrives from
 * `GET {V2}workspace/{wid}/notices` (bff-web proxy over workspace-service's
 * `workspace_notices` table) -- the SAME endpoint MetaPricingNotice.types.ts
 * reads, filtered here by a different `notice_key`.
 *
 * Written by workspace-service's MetaMessengerHealthChangedListener, which
 * consumes meta-messenger-service's `meta.messenger.health_changed` event.
 * Deliberately a SEPARATE routing key and notice_key from the real
 * disconnect flow (`meta.messenger.disconnected` /
 * `workspace.meta_messenger.disconnected`) -- see that listener's docblock
 * for the risk this avoids.
 *
 * Channels reviewer follow-up (INT-1522): keyed by meta_account_id, not by
 * channel -- a meta_account's Instagram page shares the SAME access token
 * as its Facebook page, so token health is a fact about the account, never
 * a channel in isolation. Since the key embeds a hash of the account id
 * (notice_key is varchar(64), too short for the prefix plus a full UUID --
 * see the listener), this file cannot enumerate the finite set of possible
 * keys the way the sibling pricing/cap-raise notices do: it matches any row
 * whose key starts with `META_TOKEN_INVALID_ALERT_ID`. A workspace can have
 * more than one live meta_account (the MULTI-ACCOUNT HAZARD documented on
 * the backend), so more than one such row can legitimately be live at once
 * -- the Alert Center only has one P0 slot for this family (AlertRegistry.ts),
 * so selectMetaTokenInvalidNotice() picks the first live one it finds, same
 * "one card wins" simplification the old per-channel design already made.
 *
 * THE FRONT ONLY PAINTS: the row's payload is exactly what workspace-service
 * wrote, never recomputed here.
 */

/** The alert family id in `ALERT_REGISTRY`, and the notice_key prefix every row this family owns starts with. */
export const META_TOKEN_INVALID_ALERT_ID = 'meta-messenger-token-invalid';

export function isMetaTokenInvalidNoticeKey(key: string): boolean {
  return key.startsWith(META_TOKEN_INVALID_ALERT_ID);
}

export type tMetaTokenInvalidPayload = {
  metaAccountId: string | null;
};

export type tMetaTokenInvalidNotice = {
  key: string;
  payload: tMetaTokenInvalidPayload;
  /** ISO 8601 UTC. */
  visibleFrom: string;
  expiresAt: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * The runtime guard the repo's enum rule demands (CLAUDE.md, "Runtime Enum
 * Validation for External Input"): `meta_account_id` is opaque JSON on the
 * wire. Unlike the old `channel_type` (a closed 3-value set), an account id
 * is an open-ended string, so there is nothing to validate it AGAINST except
 * its own shape -- a non-empty string, or degrade to "no account" rather
 * than reach a consumer as an unvalidated value.
 */
export function parseMetaTokenInvalidPayload(
  raw: unknown,
): tMetaTokenInvalidPayload | null {
  const source = asRecord(raw);
  if (!source) return null;

  const metaAccountId = source.meta_account_id;
  if (metaAccountId !== null && typeof metaAccountId !== 'string') return null;
  if (typeof metaAccountId === 'string' && metaAccountId.trim().length === 0) return null;

  return { metaAccountId };
}

/** The runtime guard applied to one row already matched by isMetaTokenInvalidNoticeKey(). */
export function parseMetaTokenInvalidNotice(
  raw: unknown,
): tMetaTokenInvalidNotice | null {
  const row = asRecord(raw);
  if (!row) return null;
  if (typeof row.key !== 'string' || row.key.trim().length === 0) return null;
  if (typeof row.visibleFrom !== 'string') return null;
  if (typeof row.expiresAt !== 'string') return null;

  const payload = parseMetaTokenInvalidPayload(row.payload);
  if (!payload) return null;

  return {
    key: row.key,
    payload,
    visibleFrom: row.visibleFrom,
    expiresAt: row.expiresAt,
  };
}

/**
 * The client-side expiry net, same reasoning as
 * isMetaPricingNoticeExpired(): the backend already filters on `expires_at`,
 * this only catches a tab left open past the boundary or clock drift.
 *
 * Release-review follow-up (INT-1522, promotion to main): a malformed
 * `expiresAt` resolves to EXPIRED here, unlike isMetaPricingNoticeExpired()'s
 * NaN -> not-expired. That sibling's alert is dismissible, so failing open
 * (stays visible on a bad date) costs the user one click. This alert is
 * `dismissible: false` (AlertRegistry.ts) -- failing open would strand the
 * user with a P0 they can never clear from a single malformed backend row.
 */
export function isMetaTokenInvalidNoticeExpired(
  notice: tMetaTokenInvalidNotice,
  now: number,
): boolean {
  const expiresAt = Date.parse(notice.expiresAt);
  if (Number.isNaN(expiresAt)) return true;
  return expiresAt <= now;
}

/**
 * The endpoint's whole `data` array in, this workspace's live token-invalid
 * notice out (or `null`). Scans in array order and returns the first row
 * whose key belongs to this family, parses, and is not expired -- a
 * malformed or expired row under our prefix is skipped (`continue`), not
 * treated as "nothing to show": with per-account keys, a bad row for one
 * account must never hide a live, well-formed row for a different account
 * that happens to sort after it.
 */
export function selectMetaTokenInvalidNotice(
  data: unknown,
  now: number,
): tMetaTokenInvalidNotice | null {
  if (!Array.isArray(data)) return null;
  for (const raw of data) {
    const row = asRecord(raw);
    if (!row || typeof row.key !== 'string' || !isMetaTokenInvalidNoticeKey(row.key)) continue;
    const notice = parseMetaTokenInvalidNotice(row);
    if (!notice) continue;
    if (isMetaTokenInvalidNoticeExpired(notice, now)) continue;
    return notice;
  }
  return null;
}

/**
 * Whether the response carried a row under this family's prefix that failed
 * to parse -- worth a console warning the way hasUnparsedRow() is for the
 * fixed-key sources, since the endpoint answered and this is OUR data being
 * wrong. Unlike a fixed-key source, there is no single "preferred key" to
 * compare against: every account's row is independently meaningful, so any
 * one of them failing to parse is worth flagging, regardless of whether a
 * different account's row was ultimately selected.
 */
export function hasUnparsedMetaTokenInvalidRow(data: unknown): boolean {
  if (!Array.isArray(data)) return false;
  return data.some((raw) => {
    const row = asRecord(raw);
    if (!row || typeof row.key !== 'string' || !isMetaTokenInvalidNoticeKey(row.key)) return false;

    return parseMetaTokenInvalidNotice(row) === null;
  });
}

/**
 * Whether two resolved notices say the same thing. The source publishes a
 * new reference only when this is false, so a focus refetch that returns the
 * same row does not re-render every consumer memoized on the snapshot.
 */
export function isSameMetaTokenInvalidNotice(
  a: tMetaTokenInvalidNotice | null,
  b: tMetaTokenInvalidNotice | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.key === b.key &&
    a.visibleFrom === b.visibleFrom &&
    a.expiresAt === b.expiresAt &&
    a.payload.metaAccountId === b.payload.metaAccountId
  );
}
